'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useHostSettings } from '@/lib/HostSettingsContext';
import { useHostControl } from '@/lib/HostControlContext';
import {
  fetchRecordingStatus,
  isAwaitingFileRecordingState,
  postCancelRecording,
  type RecordingUiState,
} from '@/lib/recording-status-client';
import { markRecordingAwaitingDismissedByUser } from '@/lib/recording-start-failed';
import {
  RECORDING_PREPARING_HINT_LONG,
  RECORDING_PREPARING_HINT_SLOW,
  RECORDING_PREPARING_SUBTITLE,
  RECORDING_PREPARING_TITLE,
  RECORDING_STARTING_SUBTITLE,
  RECORDING_STARTING_TITLE,
} from '@/lib/recording-ui-messages';

const POLL_MS = 2000;
const PROVISIONING_HINT_MS = 60_000;
const LONG_WAIT_MS = 90_000;

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}

export function RecordingPreparingBanner() {
  const room = useRoomContext();
  const { isHost } = useHostSettings();
  const { isCoHost } = useHostControl();
  const canControlRecording = isHost || isCoHost;

  const [visible, setVisible] = React.useState(false);
  const [phase, setPhase] = React.useState<'preparing' | 'starting'>('preparing');
  const [elapsedMs, setElapsedMs] = React.useState(0);
  const [cancelling, setCancelling] = React.useState(false);

  const preparingSinceRef = React.useRef<number | null>(null);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const roomNameRef = React.useRef<string | undefined>(room?.name);
  roomNameRef.current = room?.name;

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const stopTick = React.useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const hideBanner = React.useCallback(() => {
    setVisible(false);
    setElapsedMs(0);
    preparingSinceRef.current = null;
    stopPolling();
    stopTick();
  }, [stopPolling, stopTick]);

  const showAwaiting = React.useCallback((nextPhase: 'preparing' | 'starting') => {
    if (preparingSinceRef.current === null) {
      preparingSinceRef.current = Date.now();
    }
    setPhase(nextPhase);
    setVisible(true);
  }, []);

  const syncFromState = React.useCallback(
    (state: RecordingUiState) => {
      if (isAwaitingFileRecordingState(state)) {
        showAwaiting(state === 'starting' ? 'starting' : 'preparing');
        return;
      }
      hideBanner();
    },
    [hideBanner, showAwaiting],
  );

  const startPolling = React.useCallback(
    (roomName: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const status = await fetchRecordingStatus(roomName);
          if (isAwaitingFileRecordingState(status.state)) {
            showAwaiting(status.state === 'starting' ? 'starting' : 'preparing');
          } else {
            syncFromState(status.state);
          }
        } catch {
          /* ignore transient errors */
        }
      }, POLL_MS);
    },
    [showAwaiting, stopPolling, syncFromState],
  );

  const startTick = React.useCallback(() => {
    stopTick();
    tickRef.current = setInterval(() => {
      if (preparingSinceRef.current !== null) {
        setElapsedMs(Date.now() - preparingSinceRef.current);
      }
    }, 1000);
  }, [stopTick]);

  const handlersRef = React.useRef({
    showAwaiting,
    hideBanner,
    startPolling,
    startTick,
  });
  handlersRef.current = { showAwaiting, hideBanner, startPolling, startTick };

  React.useEffect(() => {
    const onPreparing = () => {
      handlersRef.current.showAwaiting('preparing');
      handlersRef.current.startTick();
      const name = roomNameRef.current;
      if (name) handlersRef.current.startPolling(name);
    };
    const onStarting = () => {
      handlersRef.current.showAwaiting('starting');
      handlersRef.current.startTick();
      const name = roomNameRef.current;
      if (name) handlersRef.current.startPolling(name);
    };
    const onActive = () => handlersRef.current.hideBanner();
    const onStop = () => handlersRef.current.hideBanner();

    window.addEventListener('recording:preparing', onPreparing);
    window.addEventListener('recording:starting', onStarting);
    window.addEventListener('recording:start', onActive);
    window.addEventListener('recording:stop', onStop);

    return () => {
      window.removeEventListener('recording:preparing', onPreparing);
      window.removeEventListener('recording:starting', onStarting);
      window.removeEventListener('recording:start', onActive);
      window.removeEventListener('recording:stop', onStop);
      stopPolling();
      stopTick();
    };
  }, [stopPolling, stopTick]);

  React.useEffect(() => {
    if (!room?.name) return;

    fetchRecordingStatus(room.name)
      .then((status) => {
        if (isAwaitingFileRecordingState(status.state)) {
          showAwaiting(status.state === 'starting' ? 'starting' : 'preparing');
          startTick();
          startPolling(room.name!);
        }
      })
      .catch(() => {
        /* initial sync optional */
      });
  }, [room?.name, showAwaiting, startPolling, startTick]);

  const handleCancel = async () => {
    if (!room?.name || !canControlRecording || cancelling || phase !== 'preparing') return;
    markRecordingAwaitingDismissedByUser();
    setCancelling(true);
    try {
      await postCancelRecording(room.name);
      hideBanner();
      window.dispatchEvent(new CustomEvent('recording:stop'));
    } finally {
      setCancelling(false);
    }
  };

  if (!visible) return null;

  const showSlowHint = elapsedMs >= PROVISIONING_HINT_MS;
  const showLongWait = elapsedMs >= LONG_WAIT_MS;

  return (
    <div
      className="recording-preparing-banner"
      role="status"
      aria-live="polite"
      aria-label={phase === 'starting' ? RECORDING_STARTING_TITLE : RECORDING_PREPARING_TITLE}
    >
      <div className="recording-preparing-banner__icon" aria-hidden="true">
        ⏳
      </div>
      <div className="recording-preparing-banner__content">
        <div className="recording-preparing-banner__title">
          {phase === 'starting' ? RECORDING_STARTING_TITLE : RECORDING_PREPARING_TITLE}
        </div>
        <div className="recording-preparing-banner__subtitle">
          {phase === 'starting' ? RECORDING_STARTING_SUBTITLE : RECORDING_PREPARING_SUBTITLE}
          {elapsedMs > 0 && <> · {formatElapsed(elapsedMs)}</>}
        </div>
        {showSlowHint && !showLongWait && (
          <div className="recording-preparing-banner__hint">{RECORDING_PREPARING_HINT_SLOW}</div>
        )}
        {showLongWait && (
          <div className="recording-preparing-banner__hint">
            {RECORDING_PREPARING_HINT_LONG}
            {canControlRecording && phase === 'preparing' && (
              <>
                {' '}
                <button
                  type="button"
                  className="recording-preparing-banner__cancel"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelando…' : 'Cancelar'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
